import React, {useState} from 'react';

import {Tooltip, RadioButton} from '@optimaxdev/design-system';
import {ActionCreatorWithPayload} from '@reduxjs/toolkit';
import classNames from 'classnames';
import {connect} from 'react-redux';

import {useFeature} from 'components/feature';
import {ApplicationStoreType} from 'desktop/store';
import {CurrentAddressType} from 'desktop/types/addressType';

import {CustomerAddressType} from 'desktop/types/customerAddressesType';
import {useIsOneStepAvailable} from 'hooks/useIsOneStepAvailable/useIsOneStepAvailable';

import {startUpdateCustomerAddressSaga} from '../../store/reducers/deliveryStep/customerAddresses/customerAddresses';
import {
    getAddresses,
    getAddressesActiveId,
    getAddressesDisableId,
    getAddressesSameness,
} from '../../store/selectors/deliveryStep/deliveryStep';
import {getAddressComponents} from '../../utils/formatAddress/formatAddress';
import {Addresses} from '../addresses/addresses';

import s from './customerAddressesList.scss';

export type PropsType = {
    type: CurrentAddressType;
    isSameAddress: boolean;
    activeId: Maybe<number>;
    disableId: Maybe<number>;
    addresses: Array<CustomerAddressType>;
    updateCustomerAddress: ActionCreatorWithPayload<{id: string; type: CurrentAddressType | null}>;
};

/**
 * Customer Addresses List component
 *
 * @constructor
 */
export const CustomerAddressesList: React.FC<PropsType> = ({
    activeId,
    addresses,
    disableId,
    isSameAddress,
    type,
    updateCustomerAddress,
}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const [isShowAddressesDropDown, setShowAddressesDropDown] = useState(false);

    const {isOneStepAvailable} = useIsOneStepAvailable();

    if (!addresses.length) return null;

    const toggleOpenTooltip = () => {
        setShowAddressesDropDown(value => !value);
    };

    const handleUpdateCustomerAddress = (id: string, isDisabled: boolean) => {
        if (!isDisabled) {
            updateCustomerAddress({id, type: isSameAddress ? null : type});
            toggleOpenTooltip();
        }
    };

    return (
        <Tooltip
            content={
                <div>
                    {addresses.map(address => {
                        const isChecked = activeId === Number(address.addressId);
                        const isDisabled = disableId === Number(address.addressId);
                        return (
                            <div
                                className={classNames(s.option, {
                                    [s.active]: isChecked,
                                    [s.disable]: isDisabled,
                                })}
                                key={address.addressId}
                            >
                                <RadioButton
                                    onChange={() =>
                                        handleUpdateCustomerAddress(address.addressId, isDisabled)
                                    }
                                    isChecked={isChecked}
                                    isDisabled={isDisabled}
                                    size="medium"
                                    value={address.addressId}
                                >
                                    <div className={s.addresses}>
                                        <Addresses addresses={getAddressComponents(address)} />
                                    </div>
                                </RadioButton>
                            </div>
                        );
                    })}
                </div>
            }
            isOpen={isShowAddressesDropDown}
            onChange={toggleOpenTooltip}
            position="bottom"
        >
            {isOneStepAvailable ? (
                <p className={classNames(s.link, s.oneStep)}>
                    Choose different address from my book
                </p>
            ) : (
                <p className={classNames(s.link, {[s.linkFacelift]: isFacelift})}>
                    Choose different address from my address book &gt;
                </p>
            )}
        </Tooltip>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType, {type}: {type: 'shipping' | 'billing'}) => ({
    activeId: getAddressesActiveId(store, type),
    addresses: getAddresses(store),
    disableId: getAddressesDisableId(store, type),
    isSameAddress: getAddressesSameness(store),
});

const mapDispatchToProps = {updateCustomerAddress: startUpdateCustomerAddressSaga};

export const CustomerAddressesListConnected = connect(
    mapStateToProps,
    mapDispatchToProps,
)(CustomerAddressesList);
